import { defineStore } from 'pinia'
import type { Schema } from 'yup'
import { object, string } from 'yup'
import type { StripeAccount } from '@/prisma/client'

export function stripeAccountSchema(fields: SchemaKeys[], t: Function) {
  const baseSchema = {
    accountLabel: string().required(t('errors.required')).min(3, t('errors.min', { min: 3 })),
  }
  const partialSchema: Partial<Record<SchemaKeys, Schema<any>>> = {}
  fields.forEach((fieldName) => {
    if (baseSchema[fieldName]) {
      partialSchema[fieldName] = baseSchema[fieldName]
    }
  })
  return object(partialSchema)
}

export const useStripeAccountStore = defineStore('stripeAccount', () => {
  const userStripeAccounts = ref<StripeAccount[] | null>(null)

  const isFetchingUserStripeAccounts = ref(false)
  const cacheTtl = 5 * 60 * 1000 // 5 minutes in milliseconds
  const lastFetchTime = ref<number | null>(null)

  async function startStripeOnboarding(label: string): Promise<{ data: Ref<any | null>, status: Ref<'pending' | 'success' | 'error'>, error: Ref<any | null> }> {
    const data = ref<any | null>(null)
    const status = ref<'pending' | 'success' | 'error'>('pending')
    const error = ref<any | null>(null)

    try {
      console.log(label)
      const result = await $fetch<any | null>('/api/stripe/accounts/', {
        method: 'POST',
        body: {
          label,
        },
      })

      data.value = result

      status.value = 'success'
      console.log(result)
    } catch (e: any) {
      status.value = 'error'
      error.value = e
    }

    return { data, status, error }
  }

  async function fetchUserStripeAccounts(force: boolean = false): Promise<{ data: Ref<StripeAccount[] | null>, status: Ref<'pending' | 'success' | 'error'>, error: Ref<any | null> }> {
    const data = ref<StripeAccount[] | null>(null)
    const status = ref<'pending' | 'success' | 'error'>('pending')
    const error = ref<any | null>(null)

    const now = Date.now()
    const isCacheValid = lastFetchTime.value && now - lastFetchTime.value < cacheTtl

    if (isFetchingUserStripeAccounts.value || (!force && isCacheValid && isFetchingUserStripeAccounts.value !== null)) {
      return {
        data: userStripeAccounts,
        status: ref('success'),
        error: ref(null),
      }
    }

    isFetchingUserStripeAccounts.value = true

    try {
      const result = await $fetch<StripeAccount[] | null>('/api/stripe/accounts/user')

      if (result) {
        userStripeAccounts.value = result
        lastFetchTime.value = now
      }

      data.value = result
      status.value = 'success'
    } catch (e: any) {
      status.value = 'error'
      error.value = e
    } finally {
      isFetchingUserStripeAccounts.value = false
    }

    return { data, status, error }
  }

  return {
    fetchUserStripeAccounts,
    startStripeOnboarding,
    stripeAccountSchema,
    userStripeAccounts,
  }
})
